import { useCallback, useEffect, useState } from "react";

import InventoryService from "../api/inventory.service";

import {
  Inventory,
  InventoryFilters,
  InventoryPayload,
  Pagination,
  RecordStatus,
} from "../types";

const DEFAULT_FILTERS: InventoryFilters = {
  search: "",
  page: 1,
  limit: 10,
};

export const useInventory = () => {
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [pagination, setPagination] =
    useState<Pagination>({
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    });

  const [filters, setFilters] =
    useState<InventoryFilters>(DEFAULT_FILTERS);

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response =
        await InventoryService.getInventory(
          filters
        );

      setInventory(response.items);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Failed to load inventory."
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const createInventory = async (
    payload: InventoryPayload
  ) => {
    await InventoryService.createInventory(
      payload
    );

    await fetchInventory();
  };

  const updateInventory = async (
    id: string,
    payload: InventoryPayload
  ) => {
    await InventoryService.updateInventory(
      id,
      payload
    );

    await fetchInventory();
  };

  const deleteInventory = async (
    id: string
  ) => {
    await InventoryService.deleteInventory(
      id
    );

    await fetchInventory();
  };

  const updateStatus = async (
    id: string,
    status: RecordStatus
  ) => {
    await InventoryService.updateStatus(
      id,
      status
    );

    await fetchInventory();
  };

  const updateFilters = (
    values: Partial<InventoryFilters>
  ) => {
    setFilters((prev) => ({
      ...prev,
      ...values,
    }));
  };

  return {
    inventory,

    loading,

    error,

    pagination,

    filters,

    fetchInventory,

    createInventory,

    updateInventory,

    deleteInventory,


    updateStatus,

    updateFilters,
  };
};
